/**
 * Display helpers for material slots whose `src` is property-bound.
 *
 * A bound slot's authored src is the binding string ("$material"), which is
 * what the Renderer editor used to render in the Source field — making the
 * material selector look dead on bound slots (you couldn't see the actual
 * material, and picks routed to the property never showed up). These helpers
 * give the UI the bound property's RESOLVED value to display while the
 * authored binding stays untouched underneath.
 */

import { boundPropertyName } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';

export interface BoundSrcDisplay {
    /** Property name the slot's src is bound to, or null when unbound. */
    boundTo: string | null;
    /**
     * What the Source field should show: the bound property's resolved value
     * (including active state/keyframe overrides), or the literal src when
     * unbound. Empty string for a bound property resolving to '' (native
     * material) or to a non-string.
     */
    displaySrc: string;
}

export function resolveBoundSrcDisplay(
    sceneObject: SceneObject,
    src: string | undefined,
): BoundSrcDisplay {
    const boundTo = src ? boundPropertyName(src) : null;
    if (!boundTo) return { boundTo: null, displaySrc: src ?? '' };
    const resolved = sceneObject.resolveProperty(boundTo);
    return { boundTo, displaySrc: typeof resolved === 'string' ? resolved : '' };
}

export interface BoundColorDisplay {
    /** Property name the slot's baseColorFactor is bound to, or null when unbound. */
    boundTo: string | null;
    /**
     * What the color swatch/hex field should show: the bound property's resolved
     * value (including active state overrides) or the literal color when unbound.
     * Empty string when a bound property resolves to a non-string.
     */
    displayColor: string;
}

/** Color analog of {@link resolveBoundSrcDisplay} for `baseColorFactor`. */
export function resolveBoundColorDisplay(
    sceneObject: SceneObject,
    baseColorFactor: string | undefined,
): BoundColorDisplay {
    const boundTo = baseColorFactor ? boundPropertyName(baseColorFactor) : null;
    if (!boundTo) return { boundTo: null, displayColor: baseColorFactor ?? '' };
    const resolved = sceneObject.resolveProperty(boundTo);
    return { boundTo, displayColor: typeof resolved === 'string' ? resolved : '' };
}
